import type { ReactNode } from 'react'

// Treats emptiness as an invitation to act, not a dead end — per the
// design brief's writing guidance.
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <div className="text-sm font-bold text-slate-800">{title}</div>
      {description && <div className="max-w-sm text-xs font-medium text-slate-500">{description}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-600 font-bold">
        !
      </div>
      <div className="max-w-sm text-xs font-medium text-slate-600">{message}</div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-mono text-xs font-semibold text-slate-700 hover:border-sky-300 hover:text-sky-700 shadow-sm transition-all"
        >
          Try again
        </button>
      )}
    </div>
  )
}

export function LoadingSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="animate-pulse divide-y divide-slate-100" role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3.5">
          <div className="h-3 w-16 rounded bg-slate-200" />
          <div className="h-3 w-24 rounded bg-slate-200" />
          <div className="h-3 flex-1 rounded bg-slate-200" />
          <div className="h-3 w-14 rounded bg-slate-200" />
        </div>
      ))}
    </div>
  )
}

export function StatCardSkeleton() {
  return (
    <div className="animate-pulse rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 h-3 w-20 rounded bg-slate-200" />
      <div className="h-7 w-16 rounded bg-slate-200" />
    </div>
  )
}
